import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RotateCcw, Save } from 'lucide-react';
import { pct, aud, delta, marginTone } from './roleMarginMath';

const STEPS = [-10, -5, 5, 10];

/** Per-role sell-rate (estimate currency) and cost-rate (A$) what-if editor. */
export function RoleWhatIfPanel({ data, sellRates, costRates, sim, sym, onSell, onCost, onReset, onApply, dirtySell, dirty, applying }) {
  return (
    <div className="rounded-lg border border-slate-200" data-testid="role-whatif-panel">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/60">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">What-if: role rates</p>
          <p className="text-[10px] text-slate-400">Change a sell rate ({data.currency_code}/h) or an internal cost rate (A$/h) — the matrix above updates instantly. Nothing is saved until you apply.</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onReset} disabled={!dirty} data-testid="role-whatif-reset-btn"><RotateCcw className="w-3 h-3 mr-1" />Reset</Button>
          {data.can_apply && (
            <Button size="sm" className="h-7 text-xs bg-[#0F2040] hover:bg-[#1a2f57] text-white" onClick={onApply} disabled={!dirtySell || applying} data-testid="role-whatif-apply-btn">
              <Save className="w-3 h-3 mr-1" />{applying ? 'Applying…' : 'Apply sell rates'}
            </Button>
          )}
        </div>
      </div>
      <table className="w-full text-[11px]">
        <thead className="text-slate-500">
          <tr>
            <th className="text-left px-3 py-1.5 font-semibold">Role</th>
            <th className="text-right px-2 py-1.5 font-semibold">Hours</th>
            <th className="text-left px-2 py-1.5 font-semibold">Sell rate ({sym}/h)</th>
            <th className="text-left px-2 py-1.5 font-semibold">Cost rate (A$/h)</th>
            <th className="text-right px-2 py-1.5 font-semibold">Margin now → what-if</th>
            <th className="text-right px-3 py-1.5 font-semibold">Δ margin</th>
          </tr>
        </thead>
        <tbody>
          {data.roles.map((r) => {
            const b = data.role_totals[r];
            const s = sim.role_totals[r];
            const dm = delta(s.margin_aud, b.margin_aud);
            const sellChanged = Math.abs((sellRates[r] ?? b.sell_rate_native) - b.sell_rate_native) > 0.005;
            return (
              <tr key={r} className="border-t border-slate-100" data-testid={`role-whatif-row-${r}`}>
                <td className="px-3 py-1.5 whitespace-nowrap"><span className="font-semibold text-[#0F2040]">{r}</span> <span className="text-slate-400">{b.label}</span></td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{b.hours.toFixed(1)}</td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-1">
                    <Input type="number" min="1" step="1" value={sellRates[r] ?? b.sell_rate_native} onChange={(e) => onSell(r, e.target.value)}
                      className={`h-7 w-20 text-[11px] tabular-nums ${sellChanged ? 'border-[#4ABFCF] bg-cyan-50/40' : ''}`} data-testid={`role-whatif-sell-${r}`} />
                    {STEPS.map((p) => (
                      <button key={p} type="button" onClick={() => onSell(r, Math.round(b.sell_rate_native * (1 + p / 100) * 100) / 100)}
                        className="text-[9px] px-1 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-100" data-testid={`role-whatif-sell-step-${r}-${p > 0 ? 'p' : 'm'}${Math.abs(p)}`}>{p > 0 ? '+' : ''}{p}%</button>
                    ))}
                    {sellChanged && <span className="text-[9px] text-slate-400 ml-1">was {b.sell_rate_native.toFixed(0)}</span>}
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  <Input type="number" min="0" step="1" value={costRates[r] ?? ''} placeholder={b.cost_rate_aud == null ? 'not set' : ''} onChange={(e) => onCost(r, e.target.value)}
                    className={`h-7 w-20 text-[11px] tabular-nums ${(costRates[r] ?? '') !== (b.cost_rate_aud ?? '') ? 'border-[#4ABFCF] bg-cyan-50/40' : ''}`} data-testid={`role-whatif-cost-${r}`} />
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  <span className={marginTone(b.margin_pct, data.min_margin_pct)}>{pct(b.margin_pct)}</span>
                  <span className="text-slate-300 mx-1">→</span>
                  <span className={`font-semibold ${marginTone(s.margin_pct, data.min_margin_pct)}`} data-testid={`role-whatif-margin-${r}`}>{pct(s.margin_pct)}</span>
                </td>
                <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${dm == null || Math.abs(dm) < 0.5 ? 'text-slate-400' : dm > 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-testid={`role-whatif-delta-${r}`}>
                  {dm == null || Math.abs(dm) < 0.5 ? '—' : `${dm > 0 ? '+' : '−'}${aud(Math.abs(dm))}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
